import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { dealerErrorMessage, dealersApi } from '../services/dealersApi';
import { CreditPack } from '../types/dealers';
import { ToastView, useToast } from '../components/promotions/useToast';
import DealerStatusChip from '../components/dealers/DealerStatusChip';
import FeatureOffBanner, { useFeatureOff, WalletOffNotice } from '../components/dealers/FeatureOffBanner';
import PackForm from '../components/dealers/PackForm';
import { aed, pct } from '../components/dealers/dealerFormat';

/**
 * Spec 085 Flow C / AC-19 — credit packs (route /dealers/packs, screen `dealers`). Packs belong to the dealer wallet:
 * with the wallet switch off the list stays readable but cannot be created or edited (the API refuses with
 * FEATURE_OFF, which also turns the read-only notice on for admins who cannot read the switch).
 */
export default function DealerPacksPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const { toast, show } = useToast();
  const { off, catchOff, walletOff, markWalletOff } = useFeatureOff(auth.token);
  const [packs, setPacks] = useState<CreditPack[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<CreditPack | null | 'new'>(null);

  const load = useCallback(async () => {
    if (!auth.token) return;
    setLoading(true);
    setError(null);
    try {
      setPacks(await dealersApi.packs(auth.token));
    } catch (err) {
      if (!catchOff(err)) setError(dealerErrorMessage(err, 'Failed to load credit packs'));
    } finally {
      setLoading(false);
    }
  }, [auth.token, catchOff]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="dealer-page-head">
        <button type="button" onClick={() => navigate('/dealers')} className="admin-btn admin-btn-outline admin-btn-sm">Back</button>
        <h2 className="dealer-page-title">Credit packs</h2>
        <div className="dealer-page-actions">
          {!walletOff && (
            <button type="button" className="admin-btn admin-btn-primary" onClick={() => setEditing('new')}>New pack</button>
          )}
        </div>
      </div>

      <FeatureOffBanner off={off} />
      <WalletOffNotice off={walletOff && !off} />
      <p className="dealer-muted">
        Every active dealer can buy an active pack. The dealer pays the face value less the higher of the pack&apos;s discount and its own; the two never add up.
      </p>

      <div className="admin-card">
        {loading ? (
          <div className="admin-loading" data-testid="packs-loading"><div className="admin-loading-spinner" /></div>
        ) : error ? (
          <div>
            <div className="admin-info-banner danger" role="alert">{error}</div>
            <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" onClick={load}>Retry</button>
          </div>
        ) : packs.length === 0 ? (
          <div className="admin-empty-state" data-testid="packs-empty"><p>No credit packs yet</p></div>
        ) : (
          <div className="admin-table-wrapper">
            <table className="admin-table dealer-table">
              <thead><tr><th>Name</th><th className="num">Face value</th><th className="num">Discount</th><th>Status</th><th aria-label="Actions" /></tr></thead>
              <tbody>
                {packs.map((p) => (
                  <tr key={p.id}>
                    <td style={{ fontWeight: 600 }}>{p.name}</td>
                    <td className="num">{aed(p.faceValueAed)}</td>
                    <td className="num">{pct(p.discountPct)}</td>
                    <td><DealerStatusChip status={p.status} testId="pack-status" /></td>
                    <td className="dealer-actions">
                      {!walletOff && (
                        <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" onClick={() => setEditing(p)}>Edit</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && !walletOff && auth.token && (
        <PackForm
          pack={editing === 'new' ? null : editing}
          token={auth.token}
          onClose={() => setEditing(null)}
          onSaved={(saved) => { show(editing === 'new' ? `${saved.name} created` : `${saved.name} saved`); setEditing(null); load(); }}
          onFeatureOff={markWalletOff}
        />
      )}
      <ToastView toast={toast} />
    </div>
  );
}
